import { useState, useCallback } from 'react';
import type { PendingFile } from '../components/FilePreview';

interface UploadedFile {
  file_id: string;
  filename: string;
  file_type: string;
  file_size: number;
}

interface UseFileUploadOptions {
  fileServiceUrl: string;
  partnerId: string;
  sessionId: string;
  onUploadComplete?: (file: UploadedFile) => void;
  onUploadError?: (filename: string, error: string) => void;
}

export function useFileUpload({
  fileServiceUrl,
  partnerId,
  sessionId,
  onUploadComplete,
  onUploadError,
}: UseFileUploadOptions) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  const uploadFile = useCallback(async (pendingFile: PendingFile) => {
    setPendingFiles((prev) =>
      prev.map((pf) =>
        pf.id === pendingFile.id ? { ...pf, status: 'uploading', progress: 0 } : pf
      )
    );

    try {
      const formData = new FormData();
      formData.append('file', pendingFile.file);
      formData.append('partner_id', partnerId);
      formData.append('session_id', sessionId);

      const response = await fetch(`${fileServiceUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Upload failed');
      }

      const result: UploadedFile = await response.json();

      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === pendingFile.id ? { ...pf, status: 'uploaded', progress: 100 } : pf
        )
      );

      onUploadComplete?.(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';

      setPendingFiles((prev) =>
        prev.map((pf) =>
          pf.id === pendingFile.id ? { ...pf, status: 'error', error: errorMessage } : pf
        )
      );

      onUploadError?.(pendingFile.file.name, errorMessage);
    }
  }, [fileServiceUrl, partnerId, sessionId, onUploadComplete, onUploadError]);

  const addFiles = useCallback((files: File[]) => {
    const newPendingFiles: PendingFile[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      status: 'pending' as const,
    }));

    setPendingFiles((prev) => [...prev, ...newPendingFiles]);

    // Start uploading each file
    newPendingFiles.forEach((pf) => {
      uploadFile(pf);
    });
  }, [uploadFile]);

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((pf) => pf.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setPendingFiles((prev) => prev.filter((pf) => pf.status !== 'uploaded'));
  }, []);

  const clearAll = useCallback(() => {
    console.log('[useFileUpload] clearAll called, clearing all pending files');
    setPendingFiles([]);
  }, []);

  // Get files that are ready to be sent (uploaded successfully)
  const getReadyFiles = useCallback(() => {
    return pendingFiles.filter((pf) => pf.status === 'uploaded');
  }, [pendingFiles]);

  return {
    pendingFiles,
    addFiles,
    removeFile,
    clearCompleted,
    clearAll,
    getReadyFiles,
    hasFiles: pendingFiles.length > 0,
    isUploading: pendingFiles.some((pf) => pf.status === 'uploading'),
    allUploaded: pendingFiles.length > 0 && pendingFiles.every((pf) => pf.status === 'uploaded'),
  };
}
